import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { razorpayService } from '../../services/razorpayService';
import { paidAuditService } from '../../services/paidAuditService';
import { storageService } from '../../services/storageService';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import AnnouncementBanner from '../../components/ui/AnnouncementBanner';
import Icon from '../../components/AppIcon';
import PaymentSuccessModal from './components/PaymentSuccessModal';
import RazorpayDebugPanel from '../../components/ui/RazorpayDebugPanel';
import { emailService } from '../../services/emailService';

// ── App-wide gradient (matches all other buttons in the app) ─────────────────
const GRAD        = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRAD_SHADOW = '0 4px 16px rgba(0,74,173,0.35)';

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  .pac-root * { box-sizing: border-box; }
  .pac-root { font-family: inherit; }

  /* page wrapper */
  .pac-main   { padding-top: 58px; font-family: inherit; }
  .pac-inner  { max-width: 896px; margin: 0 auto; padding: 32px 24px 64px; }

  /* page title */
  .pac-page-title {
    text-align: center;
    margin-bottom: 40px;
  }
  .pac-page-title h1 {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: clamp(24px, 5vw, 34px);
    font-weight: 700;
    color: var(--foreground, #0f172a);
    margin: 0 0 10px;
    letter-spacing: -0.02em;
  }
  .pac-page-title p {
    font-size: 16px;
    color: var(--muted-foreground, #64748b);
    margin: 0;
  }

  /* two-column grid → single column on mobile */
  .pac-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }

  /* cards */
  .pac-card {
    background: var(--card, #ffffff);
    border-radius: 16px;
    border: 1px solid var(--border, #e2e8f0);
    padding: 28px 28px 32px;
  }
  .pac-card h2 {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: var(--foreground, #0f172a);
    margin: 0;
  }

  /* currency toggle row */
  .pac-currency-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    gap: 12px;
    flex-wrap: wrap;
  }
  .pac-toggle {
    display: inline-flex;
    background: #f1f5f9;
    border-radius: 100px;
    padding: 3px;
    border: 1px solid #e2e8f0;
    flex-shrink: 0;
  }
  .pac-toggle-btn {
    padding: 5px 16px;
    border-radius: 100px;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.15s;
    font-family: inherit;
    white-space: nowrap;
  }
  .pac-toggle-btn.on  { background: ${GRAD}; color: #fff; box-shadow: 0 1px 6px rgba(0,74,173,.35); }
  .pac-toggle-btn.off { background: transparent; color: #64748b; }

  /* big price */
  .pac-price-block {
    text-align: center;
    padding: 28px 0;
    border-top: 1px solid var(--border, #e2e8f0);
    border-bottom: 1px solid var(--border, #e2e8f0);
    margin-bottom: 24px;
  }
  .pac-price-big {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: clamp(40px, 8vw, 56px);
    font-weight: 700;
    color: var(--foreground, #0f172a);
    letter-spacing: -0.03em;
    line-height: 1;
    margin-bottom: 8px;
  }
  .pac-price-sub { font-size: 13px; color: var(--muted-foreground, #64748b); margin: 0; }

  /* feature list */
  .pac-features { display: flex; flex-direction: column; gap: 16px; }
  .pac-feat      { display: flex; align-items: flex-start; gap: 12px; }
  .pac-feat p    { margin: 0; }
  .pac-feat-title { font-size: 14px; font-weight: 600; color: var(--foreground, #0f172a); margin-bottom: 2px !important; }
  .pac-feat-desc  { font-size: 12px; color: var(--muted-foreground, #64748b); }

  /* summary rows */
  .pac-summary { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; }
  .pac-sum-label {
    font-size: 11px;
    color: var(--muted-foreground, #64748b);
    margin: 0 0 3px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
  }
  .pac-sum-value { font-size: 14px; font-weight: 600; color: var(--foreground, #0f172a); margin: 0; }

  /* info boxes */
  .pac-secure-box {
    background: var(--muted, #f8fafc);
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 14px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  .pac-secure-box p { margin: 0; font-size: 12px; }
  .pac-secure-title { font-weight: 600; color: var(--foreground, #0f172a); margin-bottom: 2px !important; }
  .pac-secure-body  { color: var(--muted-foreground, #64748b); }

  .pac-upi-box {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: rgba(234,179,8,0.07);
    border: 1px solid rgba(234,179,8,0.25);
    border-radius: 10px;
    padding: 10px 14px;
    margin-bottom: 16px;
  }
  .pac-upi-box p { margin: 0; font-size: 12px; line-height: 1.6; color: #92400e; }

  /* pay button */
  .pac-pay-btn {
    width: 100%;
    padding: 15px 0;
    border-radius: 100px;
    border: none;
    background: ${GRAD};
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    font-family: inherit;
    box-shadow: ${GRAD_SHADOW};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.2s, transform 0.15s;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }
  .pac-pay-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
  .pac-pay-btn:active:not(:disabled) { opacity: 0.85; transform: translateY(0); }
  .pac-pay-btn:disabled { opacity: 0.7; cursor: not-allowed; }

  .pac-cancel-btn {
    display: block;
    width: 100%;
    margin-top: 12px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 13px;
    color: var(--muted-foreground, #64748b);
    font-family: inherit;
    text-align: center;
    transition: color 0.15s;
  }
  .pac-cancel-btn:hover:not(:disabled) { color: var(--foreground, #0f172a); }
  .pac-cancel-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* after-payment info strip */
  .pac-after {
    margin-top: 32px;
    background: var(--muted, #f8fafc);
    border-radius: 16px;
    padding: 24px 28px;
  }
  .pac-after-inner { display: flex; align-items: flex-start; gap: 16px; }
  .pac-after h3 {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: var(--foreground, #0f172a);
    margin: 0 0 12px;
  }
  .pac-after-list { display: flex; flex-direction: column; gap: 8px; }
  .pac-after-item { display: flex; align-items: flex-start; gap: 10px; }
  .pac-after-item span { font-size: 13px; color: var(--muted-foreground, #64748b); line-height: 1.5; }

  /* ── Cancelled payment panel ── */
  .pac-cancelled {
    margin-top: 20px;
    border: 1px solid rgba(180,83,9,.20);
    border-radius: 14px;
    overflow: hidden;
    font-family: inherit;
  }
  .pac-cancelled-header {
    background: rgba(180,83,9,.06);
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(180,83,9,.12);
  }
  .pac-cancelled-header-left { display: flex; align-items: center; gap: 8px; }
  .pac-cancelled-header span.label { font-size: 12px; font-weight: 700; color: #b45309; letter-spacing: .04em; }
  .pac-cancelled-dismiss { background: none; border: none; cursor: pointer; font-size: 16px; color: #b45309; line-height: 1; padding: 2px; }
  .pac-cancelled table { width: 100%; border-collapse: collapse; }
  .pac-cancelled td { padding: 9px 16px; border-bottom: 1px solid rgba(0,0,0,.04); }
  .pac-cancelled td.td-label { font-size: 11px; color: #6e6e73; font-weight: 500; width: 110px; white-space: nowrap; }
  .pac-cancelled td.td-val { font-size: 12px; font-weight: 600; color: #1d1d1f; word-break: break-all; }
  .pac-cancelled td.td-val.mono { font-family: monospace; }
  .pac-cancelled td.td-val.hi   { font-weight: 700; color: #004AAD; }
  .pac-cancelled-ref {
    padding: 10px 16px;
    background: rgba(0,74,173,.03);
    border-top: 1px solid rgba(0,0,0,.05);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
  }
  .pac-cancelled-ref .ref-label { font-size: 10px; font-weight: 700; color: #004AAD; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 2px; }
  .pac-cancelled-ref .ref-val   { font-family: monospace; font-size: 12px; font-weight: 700; color: #1d1d1f; }
  .pac-copy-btn {
    padding: 5px 12px;
    border-radius: 7px;
    border: 1px solid rgba(0,74,173,.20);
    background: #fff;
    color: #004AAD;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
    transition: background .15s, color .15s;
  }
  .pac-copy-btn.copied { background: rgba(22,163,74,.08); color: #16a34a; }
  .pac-cancelled-note {
    padding: 10px 16px;
    background: #fafafa;
    border-top: 1px solid rgba(0,0,0,.04);
    font-size: 11px;
    color: #92400e;
    line-height: 1.6;
  }
  .pac-cancelled-actions {
    padding: 12px 16px;
    border-top: 1px solid rgba(0,0,0,.05);
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .pac-retry-btn {
    flex: 1;
    min-width: 120px;
    padding: 10px 16px;
    border-radius: 9px;
    border: none;
    background: ${GRAD};
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    box-shadow: ${GRAD_SHADOW};
    transition: opacity 0.2s, transform 0.15s;
  }
  .pac-retry-btn:hover { opacity: 0.92; transform: translateY(-1px); }
  .pac-dismiss-btn {
    padding: 10px 16px;
    border-radius: 9px;
    border: 1px solid #e2e8f0;
    background: transparent;
    color: #6e6e73;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }

  /* loading screen */
  .pac-loading {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--background);
    font-family: inherit;
    text-align: center;
  }
  .pac-loading p { color: var(--color-muted-foreground); margin-top: 16px; }

  /* ── MOBILE ── */
  @media (max-width: 640px) {
    .pac-inner { padding: 24px 16px 48px; }
    .pac-page-title { margin-bottom: 28px; }
    .pac-page-title p { font-size: 14px; }
    .pac-grid { grid-template-columns: 1fr; gap: 16px; }
    .pac-card { padding: 20px 16px 24px; border-radius: 14px; }
    .pac-card h2 { font-size: 16px; }
    .pac-currency-row { flex-direction: column; align-items: flex-start; gap: 10px; }
    .pac-price-block { padding: 20px 0; }
    .pac-after { padding: 18px 16px; border-radius: 14px; }
    .pac-after-inner { flex-direction: column; gap: 10px; }
    .pac-cancelled-actions { flex-direction: column; }
    .pac-retry-btn { min-width: unset; }
  }

  @media (max-width: 360px) {
    .pac-inner { padding: 16px 12px 40px; }
    .pac-card  { padding: 16px 12px 20px; }
  }
`;

/* ─── Cancelled Payment Panel ─────────────────────────────────────────────── */
const CancelledPaymentInfo = ({ data, onRetry, onDismiss }) => {
  const [copied, setCopied] = useState(false);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const rows = [
    { label: 'Request ID', value: data?.requestId,                                                                                        mono: true,  hi: true  },
    { label: 'Order ID',   value: data?.orderId,                                                                                           mono: true             },
    { label: 'Payment ID', value: data?.paymentId || 'Not generated',                                                                      mono: true             },
    { label: 'Amount',     value: data?.amount,                                                                                            mono: false            },
    { label: 'Email',      value: data?.email,                                                                                             mono: false            },
    { label: 'Time',       value: new Date(data?.timestamp).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }),          mono: false            },
    { label: 'Status',     value: 'Cancelled by user',                                                                                     mono: false            },
  ];

  return (
    <div className="pac-cancelled">
      <div className="pac-cancelled-header">
        <div className="pac-cancelled-header-left">
          <span>⚠️</span>
          <span className="label">Payment Cancelled</span>
        </div>
        <button className="pac-cancelled-dismiss" onClick={onDismiss}>✕</button>
      </div>

      <table>
        <tbody>
          {rows.map(({ label, value, mono, hi }) => (
            <tr key={label}>
              <td className="td-label">{label}</td>
              <td className={`td-val${mono ? ' mono' : ''}${hi ? ' hi' : ''}`}>{value || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pac-cancelled-ref">
        <div>
          <div className="ref-label">Reference</div>
          <div className="ref-val">{data?.requestId || '—'}</div>
        </div>
        <button className={`pac-copy-btn${copied ? ' copied' : ''}`} onClick={() => copy(data?.requestId || '')}>
          {copied ? '✓ Copied' : 'Copy ID'}
        </button>
      </div>

      <div className="pac-cancelled-note">
        💬 Your request has been saved. You can retry payment anytime or contact support with your Request ID.
      </div>

      <div className="pac-cancelled-actions">
        <button className="pac-retry-btn" onClick={onRetry}>🔄 Retry Payment</button>
        <button className="pac-dismiss-btn" onClick={onDismiss}>Dismiss</button>
      </div>
    </div>
  );
};

/* ─── Main Screen ─────────────────────────────────────────────────────────── */
const PaidAuditCheckoutScreen = () => {
   const [searchParams] = useSearchParams();

  const currencyFromUrl = searchParams.get('currency') || 'USD';
  console.log(currencyFromUrl);

  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, userProfile } = useAuth();

  const [currency, setCurrency] = useState(currencyFromUrl);
  const [pricing,          setPricing]          = useState(null);
  const [isProcessing,     setIsProcessing]     = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submissionData,   setSubmissionData]   = useState(null);
  const [completedOrderId, setCompletedOrderId] = useState(null);
  const [showDebugPanel,   setShowDebugPanel]   = useState(false);
  const [cancelledInfo,    setCancelledInfo]    = useState(null);

  const pricing_breckdown = razorpayService.getPricingWithBreakdown(currency);

  console.log('pricing_breckdown',pricing_breckdown);

  useEffect(() => {
    if (location?.state?.submissionData) {
      setSubmissionData(location.state.submissionData);
    } else {
      toast?.error('Please upload your deck first');
      navigate('/initial-form-upload-screen');
    }
  }, [location, navigate]);

  useEffect(() => {
    const initCurrency = async () => {
      const currencyFromUrl = searchParams.get('currency');

      // ✅ 1. If URL has valid currency → use it
      if (currencyFromUrl && ['USD', 'INR'].includes(currencyFromUrl)) {
        setCurrency(currencyFromUrl);
        setPricing(razorpayService.getPricing(currencyFromUrl));
        return;
      }

      // ✅ 2. Else fallback to auto detect
      const detected = await razorpayService.detectCurrency();
      setCurrency(detected);
      setPricing(razorpayService.getPricing(detected));
    };

    initCurrency();
  }, [searchParams]);

  const handleCurrencySwitch = (c) => {
    setCurrency(c);
    setPricing(razorpayService?.getPricing(c));
  };


  const handlePayment = async () => {
    if (!user || !submissionData || !pricing) { toast?.error('Missing required information'); return; }
    setIsProcessing(true);
    setCancelledInfo(null);

    try {
      try { razorpayService?.validatePaymentConfig(); }
      catch (configError) { toast?.error(configError?.message || 'Payment gateway not configured properly'); setIsProcessing(false); return; }

      let fileUrl = submissionData?.fileUrl;
      if (submissionData?.fileObject) {
        try {
          const uploadResult = await storageService?.uploadDeckFile(submissionData.fileObject, user.id);
          fileUrl = uploadResult?.path;
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
          toast?.error('Failed to upload file. Please try again.');
          setIsProcessing(false); return;
        }
      }

      let deckSubmission;
      try {
        deckSubmission = await paidAuditService?.createPaidSubmissionBeforePayment({ ...submissionData, fileUrl });
        if (!deckSubmission?.id) throw new Error('Failed to create submission record');
      } catch (submissionError) {
        console.error('Submission creation error:', submissionError);
        toast?.error('Failed to create submission. Please try again.');
        setIsProcessing(false); return;
      }

      const tempOrderId  = `temp_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
      const paymentOrder = await paidAuditService?.createPaymentOrder({
        razorpayOrderId: tempOrderId,
        amount:          pricing.amount,
        currency:        pricing.currency,
        customerEmail:   user.email,
        customerName:    userProfile?.full_name || user.email?.split('@')?.[0],
        customerPhone:   userProfile?.mobile || '',
        submissionId:    deckSubmission.id,
        requestId:       deckSubmission.request_id,
      });
      if (!paymentOrder?.id) throw new Error('Failed to create payment record');

      await razorpayService?.openCheckout({
        amount: pricing.amount, currency: pricing.currency,
        userEmail: user.email,
        userName:  userProfile?.full_name || user.email?.split('@')?.[0],
        userPhone: userProfile?.mobile || '',

        onSuccess: async (paymentResponse) => {
          try {
            await paidAuditService?.updatePaymentOrder(paymentOrder.id, paymentResponse);
            await paidAuditService?.markSubmissionAsPaid(deckSubmission.id, paymentOrder.id);
            await paidAuditService?.updateUserSubscription(user.id, 'paid');
            await emailService.sendDeckSubmittedEmail(
              user?.email,
              user?.user_metadata?.full_name || user?.email?.split('@')[0],
              'Untitled Deck',
              'paid'
            );
            toast?.success('Payment successful! Your paid audit is being processed.');
            setCompletedOrderId(paymentOrder.id);
            setShowSuccessModal(true);
          } catch (error) {
            console.error('Post-payment processing error:', error);
            toast?.error('Payment successful but submission update failed. Please contact support.');
          } finally { setIsProcessing(false); }
        },

        onFailure: async (errorData) => {
          try { await paidAuditService?.markPaymentFailed(paymentOrder.id, errorData); toast?.error(errorData?.description || 'Payment failed. Please try again.'); }
          catch (error) { console.error('Error handling payment failure:', error); }
          finally { setIsProcessing(false); }
        },

        onCancel: async (errorData) => {
          try {
            await paidAuditService?.markPaymentCancelled(paymentOrder.id, errorData);
            setCancelledInfo({
              requestId: deckSubmission.request_id,
              orderId:   paymentOrder.id,
              paymentId: null,
              amount:    pricing.display,
              email:     user.email,
              timestamp: new Date().toISOString(),
            });
          } catch (error) {
            console.error('Error handling payment cancellation:', error);
            toast?.error('Payment cancelled.');
          } finally { setIsProcessing(false); }
        },
      });

    } catch (error) {
      console.error('Payment process error:', error);
      let msg = 'Failed to initiate payment. Please try again.';
      if      (error?.message?.includes('Razorpay Key ID not configured'))  msg = 'Payment gateway not configured. Please contact support.';
      else if (error?.message?.includes('Failed to load Razorpay SDK'))     msg = 'Failed to load payment gateway. Please check your internet connection and try again.';
      else if (error?.message)                                               msg = error.message;
      toast?.error(msg);
      setIsProcessing(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    navigate(
      completedOrderId ? '/payment-confirmation-screen' : '/user-dashboard',
      completedOrderId ? { state: { paymentOrderId: completedOrderId } } : undefined
    );
  };

  if (!pricing || !submissionData) {
    return (
      <div className="pac-loading">
        <style>{STYLES}</style>
        <div>
          <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin" />
          <p>Loading payment details...</p>
        </div>
      </div>
    );
  }

  const features = [
    ['AI-Powered Analysis',        'Advanced algorithms analyze your deck'],
    ['Human Expert Review',        'Professional analysts review your presentation'],
    ['Detailed Feedback',          'Comprehensive insights and recommendations'],
    ['Priority Processing',        'Faster turnaround time'],
    ['Actionable Recommendations', 'Specific steps to improve your deck'],
  ];

  const summaryFields = [
    ['Presentation Title', submissionData?.title],
    ['Industry',           submissionData?.industry],
    ['Target Audience',    submissionData?.audience],
    ['Purpose',            submissionData?.purpose],
    ['File',               submissionData?.fileName],
  ];

  const afterPayment = [
    'Your deck will be analyzed by our AI system within 24 hours',
    'A human expert will review the AI analysis and provide additional insights',
    "You'll receive an email notification when your audit is ready",
    'Access your detailed audit report from your dashboard',
  ];

  return (
    <>
      <style>{STYLES}</style>
      <Helmet><title>Paid Deck Audit Checkout - DecX AI</title></Helmet>

      <div style={{ minHeight: '100vh', background: 'var(--background)', fontFamily: 'inherit' }} className="pac-root">
        <RoleBasedHeader />
        <AnnouncementBanner />

        <main className="pac-main">
          <div className="pac-inner">

            {/* Page header */}
            <div className="pac-page-title">
              <h1>Paid Deck Audit</h1>
              <p>AI + Human Powered Analysis</p>
            </div>

            <div className="pac-grid">

              {/* ── Pricing Card ── */}
              <div className="pac-card">
                <div className="pac-currency-row">
                  <h2>Select Currency</h2>
                  <div className="pac-toggle">
                    {['USD', 'INR'].map(c => (
                      <button key={c} className={`pac-toggle-btn ${currency === c ? 'on' : 'off'}`} onClick={() => handleCurrencySwitch(c)}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pac-price-block">
                  <div className="pac-price-big">{pricing?.display}</div>
                  <p className="pac-price-sub">One-time payment</p>

                  {pricing_breckdown && pricing_breckdown.currency === 'INR' && (
                    <div className="pac-price-sub">
                      ₹{pricing_breckdown?.basePrice?.toLocaleString('en-IN')} + {pricing_breckdown?.gstPercentage}% GST
                    </div>
                  )}
                </div>

                
                

                <div className="pac-features">
                  {features.map(([title, desc]) => (
                    <div key={title} className="pac-feat">
                      <Icon name="CheckCircle2" size={20} color="var(--color-success, #16a34a)" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <p className="pac-feat-title">{title}</p>
                        <p className="pac-feat-desc">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Summary + Pay ── */}
              <div className="pac-card">
                <h2 style={{ marginBottom: 22 }}>Submission Summary</h2>

                <div className="pac-summary">
                  {summaryFields.map(([label, value]) => (
                    <div key={label}>
                      <p className="pac-sum-label">{label}</p>
                      <p className="pac-sum-value">{value || '—'}</p>
                    </div>
                  ))}
                </div>

                {/* Secure payment note */}
                <div className="pac-secure-box">
                  <Icon name="Shield" size={16} color="var(--color-primary, #004AAD)" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <p className="pac-secure-title">Secure Payment</p>
                    <p className="pac-secure-body">Your payment is processed securely through Razorpay. We never store your card details.</p>
                  </div>
                </div>

                {/* UPI warning */}
                <div className="pac-upi-box">
                  <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>⚠️</span>
                  <p>
                    <strong>UPI Users:</strong> Once you click "Verify and Pay" inside the payment screen,
                    the transaction is sent directly to your bank and <strong>cannot be cancelled</strong>.
                    Please confirm before proceeding.
                  </p>
                </div>

                {/* Pay button */}
                <button className="pac-pay-btn" onClick={handlePayment} disabled={isProcessing}>
                  {isProcessing ? (
                    <><Icon name="Loader2" size={18} color="#fff" className="animate-spin" /> Processing...</>
                  ) : (
                    <><Icon name="CreditCard" size={18} color="#fff" /> Pay {pricing?.display}</>
                  )}
                </button>

                <button className="pac-cancel-btn" onClick={() => navigate('/initial-form-upload-screen')} disabled={isProcessing}>
                  Cancel and go back
                </button>

                {cancelledInfo && (
                  <CancelledPaymentInfo
                    data={cancelledInfo}
                    onRetry={handlePayment}
                    onDismiss={() => setCancelledInfo(null)}
                  />
                )}
              </div>
            </div>

            {/* After payment strip */}
            <div className="pac-after">
              <div className="pac-after-inner">
                <Icon name="Info" size={22} color="var(--color-primary, #004AAD)" style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <h3>What happens after payment?</h3>
                  <div className="pac-after-list">
                    {afterPayment.map(item => (
                      <div key={item} className="pac-after-item">
                        <span style={{ color: '#004AAD', fontWeight: 700, marginTop: 1, flexShrink: 0 }}>•</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </main>
      </div>

      {showSuccessModal && (
        <PaymentSuccessModal
          isOpen={showSuccessModal}
          onClose={handleModalClose}
          orderId={completedOrderId}
          userEmail={user?.email}
          amount={pricing?.amount}
          currency={pricing?.currency}
        />
      )}

      <RazorpayDebugPanel isOpen={showDebugPanel} onClose={() => setShowDebugPanel(false)} />
    </>
  );
};

export default PaidAuditCheckoutScreen;